import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 30000 });

export async function getSchumannCurrent() {
  const { data } = await api.get("/schumann/current");
  return data;
}
export async function getSchumannHistory(days = 7) {
  const { data } = await api.get(`/schumann/history`, { params: { days } });
  return data;
}
export async function getCosmicCurrent() {
  const { data } = await api.get("/cosmic/current");
  return data;
}
export async function getCosmicHistory(days = 7) {
  const { data } = await api.get(`/cosmic/history`, { params: { days } });
  return data;
}
export async function getCosmicAlerts() {
  const { data } = await api.get("/cosmic/alerts");
  return data;
}
export async function getDailyBrief() {
  const { data } = await api.get("/daily-brief", { params: { device_id: getDeviceId() } });
  return data;
}
// Per-device journal scoping — no user accounts, so we stamp a UUID in
// localStorage on first read and pass it with every journal call.
const DEVICE_ID_KEY = "ep_device_id";
function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = (crypto.randomUUID && crypto.randomUUID()) ||
           `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

export async function listJournal() {
  const { data } = await api.get("/journal", { params: { device_id: getDeviceId() } });
  return data;
}
export async function createJournal(payload) {
  const { data } = await api.post("/journal", { ...payload, device_id: getDeviceId() });
  return data;
}
export async function deleteJournal(id) {
  const { data } = await api.delete(`/journal/${id}`, { params: { device_id: getDeviceId() } });
  return data;
}

// Birth chart
export async function createBirthChartSession(payload) {
  const { data } = await api.post("/birth-chart/create-session", payload);
  return data;
}
export async function getPaymentStatus(sessionId) {
  const { data } = await api.get(`/payments/status/${sessionId}`);
  return data;
}
export async function getBirthChartReading(sessionId) {
  const { data } = await api.get(`/birth-chart/${sessionId}`);
  return data;
}
export async function getBirthChartPricing() {
  const { data } = await api.get("/birth-chart/pricing");
  return data;
}
export function getBirthChartWheelUrl(sessionId) {
  return `${API}/birth-chart/wheel/${sessionId}.svg`;
}
export function getBirthChartPosterUrl(sessionId) {
  return `${API}/birth-chart/poster/${sessionId}.png`;
}

export async function subscribeNewsletter(email, source = "landing") {
  const { data } = await api.post("/newsletter/subscribe", { email, source });
  return data;
}

// ─── Compatibility (2-person) reading ─────────────────────────────
export async function getCompatibilityPricing() {
  const { data } = await api.get("/compatibility/pricing");
  return data;
}
export async function createCompatibilitySession(payload) {
  const { data } = await api.post("/compatibility/create-session", payload);
  return data;
}
export async function getCompatibilityReading(sessionId) {
  const { data } = await api.get(`/compatibility/${sessionId}`);
  return data;
}
export function getCompatibilityWheelUrl(sessionId) {
  return `${API}/compatibility/wheel/${sessionId}.svg`;
}
export function getCompatibilityPosterUrl(sessionId) {
  return `${API}/compatibility/poster/${sessionId}.png`;
}
export function getCompatibilityShareCardUrl(sessionId) {
  return `${API}/compatibility/share-card/${sessionId}.png`;
}

// ─── £8.88/month Unlimited-Readings Subscription ──────────────────
// Identity: email (persisted in localStorage) + device_id. No login.
const SUBSCRIBER_EMAIL_KEY = "ep_subscriber_email";
export function getSubscriberEmail() {
  try { return localStorage.getItem(SUBSCRIBER_EMAIL_KEY) || ""; }
  catch { return ""; }
}
export function setSubscriberEmail(email) {
  try {
    if (email) localStorage.setItem(SUBSCRIBER_EMAIL_KEY, email.trim().toLowerCase());
    else localStorage.removeItem(SUBSCRIBER_EMAIL_KEY);
  } catch { /* localStorage disabled */ }
}
export { getDeviceId };

export async function createSubscriptionSession({ email, origin_url, promo_code }) {
  const { data } = await api.post("/subscription/create-session", {
    email, device_id: getDeviceId(), origin_url,
    ...(promo_code ? { promo_code } : {}),
  });
  return data;
}
export async function getSubscriptionStatus({ email, session_id } = {}) {
  const e = email || getSubscriberEmail();
  const { data } = await api.get("/subscription/status", {
    params: {
      email: e || undefined,
      device_id: getDeviceId(),
      session_id: session_id || undefined,
    },
  });
  return data;
}
export async function createSubscriptionPortalSession({ email, origin_url }) {
  const { data } = await api.post("/subscription/portal-session", { email, origin_url });
  return data;
}

// ─── Referral dashboard / Next Sky Event teaser ───────────────────
export async function getReferralDashboard(code) {
  const { data } = await api.get(`/referral/dashboard/${encodeURIComponent(code)}`);
  return data;
}
export async function getNextSkyEventTeaser() {
  const { data } = await api.get("/sky-events/next-teaser");
  return data;
}
